<script setup>
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useAuthStore } from "../../stores/auth.js";
import { useSocketStore } from '../../stores/socketStore';
import { numericDate, timeOfDay } from "../../helpers/usDate";
import { useSessionStore } from '../../stores/sessionStore';
import CheckboxInput from "./shared/inputs/CheckboxInput.vue";
import GroupNameModal from "./shared/modals/GroupNameModal.vue";
import { getGroupTitle } from "../../helpers/helper";
import IconFilter from "../icons/IconFilter.vue";
import IconInfo from "../icons/IconInfo.vue";
import IconNewChat from "../icons/IconNewChat.vue";
import IconSearch from "../icons/IconSearch.vue";
import IconBreakoutRoom from "../icons/IconBreakoutRoom.vue";
import IconGroup from "../icons/IconGroup.vue";
import PgeButton from "../../pge-components/pge-button.vue";
import modal from "./shared/modals/ModalComponent.vue";
import NotificationsCount from "./shared/notifications/NotificationsCount.vue";
import SearchInput from "./shared/inputs/SearchInput.vue";
import Tooltip from "../../pge-components/pge-tooltip.vue";
import UserSettings from "./UserSettings.vue";
import Tabs from '../UI/Tabs.vue';
import SessionPeopleList from './SessionPeopleList.vue';
import PeopleWaiting from './PeopleWaiting.vue';
import BreakoutRoomModal from './BreakoutRoomModal.vue';

const props = defineProps({
  template: String,
  areas: Array,
  view: String,
  user: Object,
  newUser: Object,
  users: Array,
  allUsers: Array,
  selectedGroup: Object,
  groups: Array,
  area: Object,
  searchData: Function,
  inSession: Boolean,
  session: Object,
  sessionId: String,
  userShow: Function,
  // Hub peer-to-peer messaging toggle + the current user's hub role, used to
  // proactively block member-to-member chat creation when disabled. The server
  // enforces this authoritatively regardless.
  peerMessagingEnabled: {
    type: Boolean,
    default: true,
  },
  // The session's own "Enable In-Session Chat" toggle. When false inside a
  // session, the session chat is read-only for everyone.
  inSessionChatEnabled: {
    type: Boolean,
    default: true,
  },
  // Whether the "In Session" chats surface exists for THIS user, resolved by
  // OmniChat: false when the session toggle above is off (everyone), or when
  // hub peer-to-peer messaging is off and the user is a Member (Hub Staff / the
  // Hub Owner stay true). Always true outside a session.
  sessionChatsVisible: {
    type: Boolean,
    default: true,
  },
  currentUserRole: String,
});

const emit = defineEmits(['changeView', 'enterGroup', 'hide-chat', 'notify', 'refresh-groups']);

const GROUPS_TABS = [
  {
    id: 1,
    label: 'In Session',
  },
  {
    id: 2,
    label: 'Hub-wide',
  }
];

// State
const groupCreateModal = ref({
  visible: false,
  name: '',
  users: [],
  error: '',
});

const groupData = ref({
  visible: false,
  group: null,
  title: '',
  message: '',
});

const usersCollapsed = ref(true);
const groupsCollapsed = ref(false);
const groupSelected = ref(false);
const isAttendeesSearchVisible = ref(false);
const isGroupSearchVisible = ref(false);
const groupSearch = ref('');
const selectedUsers = ref([]);
const halfMarked = ref(false);
const currentUser = ref({});
const tooltipStatus = ref(localStorage.getItem("showTooltip") === "false" ? false : true);
const tooltip = ref({
  visible: false,
  target: null,
  targetLeft: false,
  text: "",
  initial: "bottom",
  xPosition: "center"
});
const authStore = useAuthStore();
const socketStore = useSocketStore();
const sessionStore = useSessionStore();

// Everybody in this room, you included (People lists you, as "You"): in a
// breakout, that room's people; in the main room, everybody not out in one.
const roomPeople = computed(() => {
  const inBreakouts = sessionStore.isInBreakout ? null : new Set(sessionStore.breakoutRooms.flatMap(r => r.participantIdentities || []));
  const room = sessionStore.isInBreakout ? sessionStore.breakoutRooms.find(r => r.roomName === sessionStore.breakoutRoomName) : null;
  const localId = sessionStore.localParticipantIdentity;

  return sessionStore.sessionParticipants
    .filter((p) => {
      if (p.participantIdentity === localId) return true;
      if (room) return room.participantIdentities.includes(p.participantIdentity);
      return !inBreakouts?.has(p.participantIdentity);
    })
    // The room knows names; the hub's member list has their photos.
    .map((p) => {
      if (p.avatar) return p;
      const user = props.allUsers?.find(u => String(u.user_id) === String(p.participantIdentity));
      return user?.avatar ? { ...p, avatar: user.avatar } : p;
    });
});

// Office Hours is a call, whatever the store's webinar flag (anything but a
// video conference) makes of it: no stage, no audience.
const isOfficeHoursRoom = computed(() => props.session?.video_call_type === 'office_hours');

// Taking somebody out: off the stage and their hand down first, so the stage
// and the audience read right the moment they are gone (the room's actions
// run in order), then out of the room.
const removePerson = (identity, name) => {
  const person = sessionStore.sessionParticipants.find((p) => p.participantIdentity === identity);
  if ((sessionStore.onStageIdentities ?? []).includes(identity)) sessionStore.requestMoveOffStage(identity);
  if (person?.isHandRaised) sessionStore.requestLowerHand(identity);
  sessionStore.requestRemoveParticipant(identity, name);
};

// Bring on: on to the stage, and a hand that was up comes down with it
// (prototype stageBring). The hand is never the way there.
const bringOn = (identity) => {
  const person = sessionStore.sessionParticipants.find((p) => p.participantIdentity === identity);
  sessionStore.requestMoveToStage(identity);
  if (person?.isHandRaised) sessionStore.requestLowerHand(identity);
};

// Waiting to come in: shown above the room for whoever can let them in (PeopleWaiting).
const waitingShown = computed(() => props.inSession && sessionStore.isAdmin && !!sessionStore.waitingActions && (sessionStore.waitingAttendees ?? []).length > 0);
const timeout = ref(null);
const breakoutModalData = ref(null); // { participantIdentity, participantName }

const isLocalSpeaker = computed(() => {
  const localId = sessionStore.localParticipantIdentity;
  const localParticipant = sessionStore.sessionParticipants.find(p => p.participantIdentity === localId);
  return localParticipant?.role === 'speaker';
});

// In a session, People is that session's people only — no hub directory, no
// search across the hub (In-session panel: only this session's people). One
// list, so no tab strip over it either.
const peopleTabs = computed(() => {
  const localId = sessionStore.localParticipantIdentity;
  const inSessionCount = sessionStore.sessionParticipants.filter(u => !u.invisible && u.participantIdentity !== localId).length;
  return [
    {
      id: 1,
      label: `In Session (${inSessionCount})`,
    },
  ];
});

// Computed

// The In Session chats sub-tab disappears when "Enable In-Session Chat" is off
// (for Members, Hub Staff and Hub Owner alike) or when hub peer-to-peer
// messaging is off and the viewer is a Member — Hub Staff / the Hub Owner keep
// it. Either way the Chats tab and its Hub-wide sub-tab stay: one-to-one and
// hub group chats are not governed by a per-session toggle, and members must
// keep being able to reach Hub Staff / the Hub Owner.
const showInSessionGroupsTab = computed(() => !props.inSession || props.sessionChatsVisible);

const groupsTabs = computed(() =>
  showInSessionGroupsTab.value ? GROUPS_TABS : GROUPS_TABS.filter(t => t.id !== 1)
);

// Tabs defaults to activeTab 1; with that tab removed nothing would be active
// and its (empty) panel would still render, so point the default at Hub-wide.
const defaultGroupsTab = computed(() => (showInSessionGroupsTab.value ? 1 : 2));

// Filter groups for Active Session Chats tab
const sessionGroups = computed(() => {
  if (!showInSessionGroupsTab.value) return [];
  return props.groups?.filter(g => g.isSessionGroup && String(g.sessionId) === String(props.sessionId)) || [];
});

// Filter groups for All Chats tab (shows private chats and main session chat, but hiding breakout chats)
const regularGroups = computed(() => {
  const mainSessionId = props.session ? String(props.session.id) : null;

  return props.groups?.filter(g => {
    if (!g.isSessionGroup) return true; // Show private chats

    // Outside a live session (hub-wide chat) session chats are retained and
    // stay visible to their members. Inside a session this tab still shows only
    // the main-session chat (breakout chats live in the In Session tab).
    if (!mainSessionId) return true;
    return String(g.sessionId) === mainSessionId;
  }) || [];
});

const getLoggedInUserEmail = computed(() => {
  return authStore.userData.email;
});

const formatGroupMessageTime = computed(() => {
  return (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    switch (true) {
      case date.toDateString() === today.toDateString():
        return timeOfDay(date);
      case date.toDateString() === yesterday.toDateString():
        return "Yesterday";
      case date < new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7):
        return numericDate(date);
      case date < yesterday:
        const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        return days[date.getDay()];
      default:
        return timeOfDay(date);
    }
  };
});

const hideInChat = computed(() => {
  if(props.newUser) return props.newUser.invisible;
  return false;
});

// Methods
const handleTooltipMouseEnter = async (e, text, initial = "bottom", xPosition = "center") => {
  if (selectedUsers.value.length && initial === 'top') return;
  if (tooltip.value.target !== e.target) {
    tooltip.value.visible = false;
  }
  await nextTick();
  tooltip.value.target = e.target;
  tooltip.value.text = text;
  tooltip.value.initial = initial;
  tooltip.value.xPosition = xPosition;
  tooltip.value.targetLeft = false;
  tooltip.value.visible = true;
};

const handleTooltipMouseLeave = () => {
  if (selectedUsers.value.length && tooltip.value.initial === 'top') return;
  tooltip.value.targetLeft = true;
};

const handleTooltipClose = () => {
  tooltip.value.visible = false;
  tooltip.value.targetLeft = false;
  tooltip.value.target = null;
  tooltip.value.text = "";
  tooltip.value.initial = "bottom";
  tooltip.value.xPosition = "center";
};

const toggleAttendeesSearchVisible = () => {
  isAttendeesSearchVisible.value = !isAttendeesSearchVisible.value;
  if (isAttendeesSearchVisible.value) {
    handleTooltipClose();
  }
};

const toggleGroupSearchVisible = () => {
  isGroupSearchVisible.value = !isGroupSearchVisible.value;
  if (isGroupSearchVisible.value) {
    handleTooltipClose();
  }
};

const handleToggleHiddenChat = (status) => {
  emit('hide-chat', status);
};

const handleHiddenTooltip = () => {
  tooltipStatus.value = false;
  localStorage.setItem("showTooltip", "false");
};

const handleShowTooltip = () => {
  tooltipStatus.value = true;
};

const handleGroupSearch = (e) => {
  const value = e.target.value;
  groupSearch.value = value;
  props.searchData("group", value);
};

const handleSelectAll = (e) => {
  const loggedUserId = Number(authStore.userData.id);
  selectedUsers.value = e.target.checked
    ? props.allUsers.filter((user) => user.user_id !== loggedUserId && !isUserRestricted(user))
    : [];

  if (selectedUsers.value.length > 0) {
    tooltipStatus.value = false;
  }
};

const handleGroupsTransitionEnd = () => {
  if (groupsCollapsed.value) {
    groupsCollapsed.value = false;
    groupSelected.value = false;
  }
};

const handleNewChat = () => {
  emit('changeView', 'attendees');
};

const getUserFullName = (group) => {
  if (group.name === "one-to-one") {
    let user = group.users.filter(
      (i) => i.email !== getLoggedInUserEmail.value
    );

    if (user.length > 0) {
      return user[0].fullName;
    } else {
      return group.name;
    }
  } else {
    return group.name;
  }
};

const intersect = (a, b) => {
  return a.filter(Set.prototype.has, new Set(b));
};

// Hub Staff / Hub Owner keep full OmniChat access. Mirrors the websocket's
// HUB_STAFF_ROLES so the client-side gate matches server enforcement.
const HUB_STAFF_ROLES = ['owner', 'event-planner', 'admin', 'administrator', 'super-admin'];
const isStaffOrOwner = (role) => HUB_STAFF_ROLES.includes(String(role || '').toLowerCase());

// When peer messaging is off and the current user is a Member, they may only
// start chats that include Hub Staff / the Hub Owner. Returns true if blocked.
const isPeerMessagingBlocked = (targetUsers) => {
  if (props.peerMessagingEnabled) return false;
  if (isStaffOrOwner(props.currentUserRole)) return false;
  return (targetUsers || []).some((u) => u && !isStaffOrOwner(u.role));
};

const RESTRICTION_TOOLTIP =
  "You can't start a chat with this member. You can still chat with Hub Staff and Hub Owner.";

// The current user is a Member and peer messaging is off: regular members become
// non-selectable — only Hub Staff / Hub Owner can be chatted.
const isMemberRestricted = computed(
  () => !props.peerMessagingEnabled && !isStaffOrOwner(props.currentUserRole)
);

// A specific member row is restricted (can't be chatted) under that rule.
const isUserRestricted = (user) => isMemberRestricted.value && !isStaffOrOwner(user?.role);

// New Chat is enabled only with a valid target selected: normally any user, but
// when restricted at least one Hub Staff / Hub Owner.
//
// Hub chat only. Nothing in a session starts a conversation (XN-5012): the
// controls are not built there, and createGroups refuses in a session too.
const canStartChat = computed(() => {
  if (!isMemberRestricted.value) return selectedUsers.value.length > 0;
  return selectedUsers.value.some((u) => isStaffOrOwner(u.role));
});

const createGroups = (usersToCreate = null) => {
  // A session has one conversation, its own; nothing in it starts another.
  if (props.inSession) return;
  const loggedUser = props.allUsers.find(
    (u) => u.user_id === Number(authStore.userData.id)
  );
  // Use provided users or fallback to selectedUsers
  const targetUsers = usersToCreate || selectedUsers.value;

  if (isPeerMessagingBlocked(targetUsers)) {
    emit(
      "notify",
      "Member-to-member messaging is disabled for this hub. You can still message Hub Staff and the Hub Owner."
    );
    return;
  }
  const groupUsers = [...targetUsers, loggedUser];

  const selectedUsersIds = groupUsers
    .filter((u) => u && u.user_id)
    .map((u) => u.user_id);

  const matchGroups = props.groups.filter((g) => {
    // Never treat the session chat as an existing version of the chat being
    // started. It is auto-created for everyone in the room, so in a two-person
    // session it holds exactly the same members as a one-to-one between them —
    // and offering to open it instead ("Do you want to open the chat 'Tes'?")
    // silently redirects the user to a different conversation. The server
    // applies the same exclusion in Chat.groupAdd.
    if (g.isSessionGroup) return false;

    if (g.domain === window.store.subdomain) {
      const groupUsersIds = [
        ...g.users,
        ...g.pendingUsers,
        ...g.declineUsers,
      ]
        .map((u) => u.user_id)
        .filter((uId) => uId);
      const uniqueGroupUsersIds = [...new Set(groupUsersIds)];

      const matchUsers = intersect(
        selectedUsersIds,
        uniqueGroupUsersIds
      );

      return (
        matchUsers &&
        matchUsers.length === groupUsersIds.length &&
        matchUsers.length === selectedUsersIds.length
      );
    }
  });

  if (matchGroups.length > 0) {
    groupData.value = {
      group: matchGroups[0],
      title: `This Chat Already Exists`,
      message: `Do you want to open the chat "${matchGroups[0].name}"?`,
      visible: true,
    };
  } else {
    groupCreateModal.value = {
      name: getGroupTitle(null, groupUsers, authStore.userData.id),
      users: groupUsers,
      error: "Please enter group name!",
      visible: true,
    };
  }
};


const currentBreakoutRoom = computed(() => {
  if (!sessionStore.isInBreakout) return null;
  return sessionStore.breakoutRooms.find(r => r.roomName === sessionStore.breakoutRoomName);
});

const totalBreakoutParticipants = computed(() => {
  return sessionStore.breakoutRooms.reduce((acc, room) => acc + (room.participantIdentities?.length || 0), 0);
});

// Breakout countdown timer (only when admin set a time limit)
const breakoutTimeRemaining = ref(null);
const formattedBreakoutTimeRemaining = computed(() => {
  if (breakoutTimeRemaining.value === null) return '';
  const minutes = Math.floor(breakoutTimeRemaining.value / 60);
  const seconds = breakoutTimeRemaining.value % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
});

let breakoutTimerInterval = null;

const startBreakoutCountdown = () => {
  if (breakoutTimerInterval) {
    clearInterval(breakoutTimerInterval);
    breakoutTimerInterval = null;
  }
  if (!sessionStore.breakoutEndTime) {
    breakoutTimeRemaining.value = null;
    return;
  }
  const update = () => {
    const now = new Date();
    const endTime = new Date(sessionStore.breakoutEndTime);
    const diff = Math.floor((endTime - now) / 1000);
    breakoutTimeRemaining.value = diff > 0 ? diff : 0;
    if (diff <= 0 && breakoutTimerInterval) {
      clearInterval(breakoutTimerInterval);
      breakoutTimerInterval = null;
    }
  };
  update();
  breakoutTimerInterval = setInterval(update, 1000);
};

watch(() => sessionStore.isInBreakout, (inBreakout) => {
  if (inBreakout && sessionStore.breakoutEndTime) {
    startBreakoutCountdown();
  } else {
    if (breakoutTimerInterval) {
      clearInterval(breakoutTimerInterval);
      breakoutTimerInterval = null;
    }
    breakoutTimeRemaining.value = null;
  }
}, { immediate: true });

watch(() => sessionStore.breakoutEndTime, (endTime) => {
  if (sessionStore.isInBreakout && endTime) {
    startBreakoutCountdown();
  } else {
    breakoutTimeRemaining.value = null;
  }
});

const createGroup = (name) => {
  if (groupCreateModal.value.name) {
    socketStore.socket.emit(
      "user_group",
      {
        domain: window.store.subdomain,
        name: name ? name : groupCreateModal.value.name,
        userId: authStore.userData.id,
        users: groupCreateModal.value.users,
        // Hub chats only: nothing in a session starts a conversation (XN-5012).
        isSessionGroup: false,
      },
      (result) => {
        // Server declined (peer messaging disabled) — surface and bail out.
        if (result && result.success === false) {
          groupCreateModal.value = { visible: false };
          emit("notify", result.error || "Messaging is restricted for this hub.");
          return;
        }
        // Match on either key: the create path returns an aggregated group
        // (`groupId`), a duplicate the aggregate cannot see falls back to the raw
        // document (`_id`).
        const resultId = result.group?.groupId ?? result.group?._id;
        const existingGroup = props.groups.find(
          (g) => resultId != null && String(g.groupId ?? g._id) === String(resultId)
        );

        if (result.duplicate) {
          groupCreateModal.value = { visible: false };
          selectedUsers.value = [];

          // Already in this user's list: open it. Reaching this chat is exactly
          // what they were trying to do, so an error helps nobody.
          if (existingGroup) {
            usersCollapsed.value = false;
            groupsCollapsed.value = true;
            emit("enterGroup", existingGroup);
            return;
          }

          // It exists but is not theirs to open — an invitation they have not
          // accepted yet. The old copy told them to "select from the list", which
          // is the one thing that cannot work: it is not in the list.
          emit("notify",
            'You already have a chat with these members. Check your notifications to accept the invitation.'
          );
          return;
        }
        selectedUsers.value = [];
        usersCollapsed.value = false;
        groupsCollapsed.value = true;
        emit("enterGroup", result.group);
        groupCreateModal.value = {
          visible: false,
        };
      }
    );
  }
};

const selectGroup = (group) => {
  usersCollapsed.value = false;
  groupSelected.value = true;
  emit("enterGroup", group);
};

const handlePeopleAction = (action, participantIdentity, participantName) => {
  if (action === 'mute') sessionStore.requestMuteParticipant(participantIdentity);
  if (action === 'unmute') sessionStore.requestUnmuteParticipant(participantIdentity);
  if (action === 'stop-video') sessionStore.requestStopVideo(participantIdentity);
  if (action === 'start-video') sessionStore.requestStartVideo(participantIdentity);
  if (action === 'lower-hand') sessionStore.requestLowerHand(participantIdentity);
  if (action === 'remove') sessionStore.requestRemoveParticipant(participantIdentity, participantName);
  if (action === 'add-to-breakout') {
    breakoutModalData.value = { participantIdentity, participantName };
  }
  if (action === 'switch-rooms') {
    sessionStore.requestSwitchBreakoutRoom();
  }
  if (action === 'return-to-main') {
    sessionStore.requestReturnToMain();
  }
};

const handleBreakoutMove = ({ participantIdentity, participantName, targetRoomName }) => {
  sessionStore.requestMoveToBreakout(participantIdentity, participantName, targetRoomName);
  breakoutModalData.value = null;
};


// Watchers
watch(() => props.view, (newView) => {
  usersCollapsed.value = newView === "attendees";
  if (!groupSelected.value) {
    groupsCollapsed.value = newView === "groups";
  }
});

watch(selectedUsers, (newVal) => {
  halfMarked.value = newVal.length && newVal.length < props.users?.length;
  nextTick(() => {
    if (newVal.length > 0) {
      handleHiddenTooltip();
    }
  });
}, { deep: true });

watch(() => sessionStore.sessionParticipants.length, (newCount) => {
  if (props.inSession && newCount >= 2 && sessionGroups.value.length === 0) {
    if (timeout.value) clearTimeout(timeout.value);
    timeout.value = setTimeout(() => {
        emit('refresh-groups');
    }, 2000);
  }
});

// Lifecycle
onMounted(() => {
  currentUser.value = authStore.userData || {};

  usersCollapsed.value = props.view === "attendees";
  if (!groupSelected.value) {
    groupsCollapsed.value = props.view === "groups";
  }
});

onBeforeUnmount(() => {
  if (breakoutTimerInterval) {
    clearInterval(breakoutTimerInterval);
    breakoutTimerInterval = null;
  }
});
</script>

<template>
  <div class="users__content xl-rm" @transitionend="handleGroupsTransitionEnd">
    <tooltip
      v-if="tooltip.visible"
      :text="tooltip.text"
      :initial="tooltip.initial"
      :x-position="tooltip.xPosition"
      :target-left="tooltip.targetLeft"
      :rect="tooltip.target?.getBoundingClientRect()"
      @close="handleTooltipClose"
    />
    <div class="users">
      <div
        :class="{
          users__list: true,
          'users__list--active': usersCollapsed,
          'has-user': selectedUsers.length > 0,
        }"
      >
        <template v-if="usersCollapsed">
          <tabs
            v-if="inSession"
            :tabs="peopleTabs"
            v-slot="{ active }"
            class="people-tabs--single"
            content-class="users__list users__list--active"
          >
            <template v-if="active === 1">
                <!-- No Mute All, no Stop Video, no search over the list: People is the
                     session's people, in groups, with a control on each row (prototype
                     People: "No search, no filters, no checkboxes"). -->
               <div v-if="sessionStore.isInBreakout" class="breakout-tenant-controls">
                 <div class="breakout-stats-row">
                   <div class="breakout-stats-left">
                     <span class="breakout-room-name">{{ sessionStore.currentRoomDisplayName }}</span>
                   </div>
                   <div class="breakout-stats-right">
                     <div class="breakout-stat-item"
                          @mouseenter="(e) => handleTooltipMouseEnter(e, 'Number of breakout rooms currently active', 'top')"
                          @mouseleave="handleTooltipMouseLeave"
                     >
                       <IconBreakoutRoom id="user-icon-breakout-room" size="14" color="#867D8D"/>
                       <span>{{ sessionStore.breakoutRooms.length }}</span>
                     </div>
                     <div class="breakout-stat-item"
                          @mouseenter="(e) => handleTooltipMouseEnter(e, 'Total participants currently assigned to breakout rooms', 'top')"
                          @mouseleave="handleTooltipMouseLeave"
                     >
                       <IconGroup size="14" color="#867D8D"/>
                       <span>{{ totalBreakoutParticipants }}</span>
                     </div>
                     <div class="breakout-stat-divider"></div>
                     <div class="breakout-stat-live">
                       <span class="breakout-live-dot"></span> Live
                     </div>
                     <div v-if="breakoutTimeRemaining !== null" class="breakout-stat-time"
                          @mouseenter="(e) => handleTooltipMouseEnter(e, 'Time remaining before breakout rooms close', 'top', 'right')"
                          @mouseleave="handleTooltipMouseLeave"
                     >
                       {{ formattedBreakoutTimeRemaining }}
                     </div>
                   </div>
                 </div>
                 <div class="breakout-action-buttons">
                    <PgeButton
                      v-if="sessionStore.breakoutAllowLeaveEarly || sessionStore.isAdmin || isLocalSpeaker"
                      class="users-quiet-btn"
                      template="text-neutral"
                      label="Leave Breakout"
                      icon="Back"
                      button-size="small"
                      @click="handlePeopleAction('return-to-main')"
                    />
                    <PgeButton
                      v-if="sessionStore.breakoutAttendeesChoose"
                      class="users-quiet-btn"
                      template="text-neutral"
                      label="Switch Rooms"
                      icon="Swap"
                      button-size="small"
                      @click="handlePeopleAction('switch-rooms')"
                    />
                 </div>
               </div>

               <div class="people-list no-scrollbar" style="overflow-y: auto; height: 100%;">
                 <!--
                   Who is waiting to come in, above the room (prototype People,
                   cd112: "Waiting sits ABOVE the room, because it is the only
                   part of this list with an action attached"). For whoever can
                   let them in; each stays until admitted, declined or gone.
                 -->
                 <PeopleWaiting v-if="waitingShown" :room-heading="false" :phone="sessionStore.phoneRoom" />
                 <!-- The groups follow the format: a webinar's On stage and
                      Audience; a call's In this session (Office Hours and a
                      breakout are calls). A raised hand is marked on its row,
                      numbered in the queue. -->
                 <SessionPeopleList
                    :people="roomPeople"
                    :webinar="sessionStore.isWebinar && !sessionStore.isInBreakout && !isOfficeHoursRoom"
                    :local-identity="sessionStore.localParticipantIdentity"
                    :on-stage-identities="sessionStore.onStageIdentities"
                    :can-act="sessionStore.isAdmin"
                    :hand-queue="sessionStore.handQueue"
                    :phone="sessionStore.phoneRoom"
                    @bring-on="bringOn"
                    @off-stage="(id) => sessionStore.requestMoveOffStage(id)"
                    @remove="removePerson"
                    @lower-hand="(id) => sessionStore.requestLowerHand(id)"
                    @mute="(id) => sessionStore.requestMuteParticipant(id)"
                    @mute-all="sessionStore.requestMuteAll('audio')"
                 />
               </div>

            </template>
          </tabs>

          <div v-else class="users__list-wrapper">
            <div class="users__list-header">
              <div class="custom-check custom-check--all">
                <checkbox-input
                  id="select-all"
                  aria-label="Select all"
                  :checked="selectedUsers.length > 0"
                  type="all"
                  :half-marked="halfMarked"
                  :disabled="hideInChat"
                  :onChange="handleSelectAll"
                  @mouseenter="(e) => handleTooltipMouseEnter(e, selectedUsers.length > 0 ? 'Deselect all' : 'Select all')"
                  @mouseleave="handleTooltipMouseLeave"
                />
              </div>
              <div class="users__list-search-container">
                <user-settings
                  :user="currentUser"
                  :newUser="newUser"
                  :tooltip="tooltipStatus"
                  :in-session="inSession"
                  @change="handleToggleHiddenChat"
                  @hiddenTooltip="handleHiddenTooltip"
                  @showTooltip="handleShowTooltip"
                />
                <search-input
                  class="users-search-input-header"
                  placeholder="Search for attendees"
                  small
                  @input="(e) => searchData('user', e.target.value)"
                />
                <div class="search-container users-search-input-action">
                  <div class="search__actions">
                    <button
                      class="filter"
                      :class="isAttendeesSearchVisible ? 'btn-active' : ''"
                      :disabled="users.length === 0"
                      aria-label="Search"
                      :aria-pressed="isAttendeesSearchVisible"
                      @click="toggleAttendeesSearchVisible"
                      @mouseenter="(e) => !isAttendeesSearchVisible ? handleTooltipMouseEnter(e, 'Search') : undefined"
                      @mouseleave="!isAttendeesSearchVisible ? handleTooltipMouseLeave() : undefined"
                    >
                      <icon-search />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="search-panel user-search-panel" v-if="isAttendeesSearchVisible">
              <search-input
                placeholder="Search for attendees"
                small
                @input="(e) => searchData('user', e.target.value)"
              />
            </div>
            <ul v-if="users.length > 0 && usersCollapsed" class="users__list-body no-scrollbar">
              <li
                v-for="user in users"
                :key="user.id"
                :class="{ 'custom-check': true }"
              >
                <checkbox-input
                  v-model="selectedUsers"
                  :value="user"
                  :name="inSession ? `user-session-${user.user_id}` : `user-${user.user_id}`"
                  :id="inSession ? `user-session-${user.user_id}` : `user-${user.user_id}`"
                  :disabled="hideInChat || isUserRestricted(user)"
                  multiple
                />
                <label :for="inSession ? `user-session-${user.user_id}` : `user-${user.user_id}`" class="users__wrapper">
                  <div class="users__name">
                    <div class="users__picture">
                      <div class="avatar">
                        <img v-if="user.avatar" :src="user.avatar" alt="" />
                        <svg
                          v-else
                          width="32"
                          height="32"
                          viewBox="0 0 40 40"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            x="0.25"
                            y="0.25"
                            width="39.5"
                            height="39.5"
                            rx="19.75"
                            stroke="#CEC3CE"
                            stroke-width="0.5"
                          />
                          <path
                            d="M29.6503 27.8756C28.2225 25.4072 26.0222 23.6372 23.4544 22.7981C24.7246 22.042 25.7114 20.8898 26.2634 19.5185C26.8153 18.1473 26.9019 16.6327 26.5098 15.2075C26.1177 13.7823 25.2685 12.5252 24.0928 11.6292C22.9171 10.7333 21.4798 10.248 20.0016 10.248C18.5234 10.248 17.0861 10.7333 15.9104 11.6292C14.7346 12.5252 13.8855 13.7823 13.4934 15.2075C13.1013 16.6327 13.1879 18.1473 13.7398 19.5185C14.2918 20.8898 15.2786 22.042 16.5488 22.7981C13.981 23.6362 11.7807 25.4062 10.3528 27.8756C10.3005 27.961 10.2658 28.056 10.2507 28.155C10.2357 28.254 10.2406 28.355 10.2652 28.4521C10.2898 28.5492 10.3337 28.6404 10.3941 28.7202C10.4545 28.8001 10.5303 28.867 10.6171 28.9171C10.7038 28.9672 10.7997 28.9995 10.899 29.0119C10.9984 29.0243 11.0993 29.0167 11.1957 28.9896C11.2921 28.9624 11.3821 28.9162 11.4603 28.8537C11.5386 28.7912 11.6035 28.7136 11.6513 28.6256C13.4175 25.5731 16.5394 23.7506 20.0016 23.7506C23.4638 23.7506 26.5857 25.5731 28.3519 28.6256C28.3997 28.7136 28.4646 28.7912 28.5429 28.8537C28.6211 28.9162 28.7111 28.9624 28.8075 28.9896C28.9039 29.0167 29.0048 29.0243 29.1042 29.0119C29.2035 28.9995 29.2994 28.9672 29.3861 28.9171C29.4729 28.867 29.5487 28.8001 29.6091 28.7202C29.6695 28.6404 29.7134 28.5492 29.738 28.4521C29.7626 28.355 29.7675 28.254 29.7525 28.155C29.7374 28.056 29.7027 27.961 29.6503 27.8756ZM14.7516 17.0006C14.7516 15.9622 15.0595 14.9472 15.6364 14.0839C16.2133 13.2205 17.0332 12.5476 17.9925 12.1502C18.9518 11.7529 20.0074 11.6489 21.0258 11.8515C22.0442 12.054 22.9797 12.5541 23.7139 13.2883C24.4481 14.0225 24.9481 14.958 25.1507 15.9764C25.3533 16.9948 25.2493 18.0504 24.852 19.0097C24.4546 19.969 23.7817 20.7889 22.9183 21.3658C22.055 21.9427 21.0399 22.2506 20.0016 22.2506C18.6097 22.2491 17.2752 21.6955 16.2909 20.7113C15.3067 19.727 14.7531 18.3925 14.7516 17.0006Z"
                            fill="#4B444D"
                          />
                        </svg>
                      </div>
                      <span
                        :class="{
                          users__status: true,
                          'users__status--online': user.online,
                          'users__status--offline': !user.online,
                        }"
                      ></span>
                    </div>
                    <strong>{{ user.fullName }}</strong>
                  </div>
                </label>
                <pge-button
                  v-if="isUserRestricted(user)"
                  class="users__restriction-icon"
                  template="text-neutral"
                  button-size="tiny"
                  icon="MessageOff"
                  :tooltip="RESTRICTION_TOOLTIP"
                  tooltip-initial="left"
                  tooltip-x-position="center"
                />
                <button class="users__info_button" @click="() => userShow(user)">
                  <icon-info size="16" color="#B3B3B3" />
                </button>
              </li>
            </ul>
            <div v-else-if="!users.length && usersCollapsed" class="no--users">
              <span>
                You are the only one here
              </span>
            </div>
            <div
              :class="[
                'start-group',
                { 'active': canStartChat, 'in-session': inSession }
              ]"
            >
              <button
                class="chat"
                @click="canStartChat ? createGroups() : null"
                @mouseenter="(e) => handleTooltipMouseEnter(e, 'Select some attendees to start a new chat', 'top', 'right')"
                @mouseleave="handleTooltipMouseLeave"
              >
                <div class="state-layer" />
                <icon-new-chat />
                <span>New Chat</span>
              </button>
            </div>
          </div>
        </template>
      </div>

      <!-- The hub's list of chats, with its ways to start one. Not built in a
           session: its one conversation is its own chat (XN-5012), and nothing
           in the room starts another. -->
      <div
        v-if="!inSession"
        :class="{
          users__list: true,
          'users__list--active': groupsCollapsed || template === 'full' || view === 'groups'
        }"
      >
        <div class="group__list-header">
          <div class="search__actions new_chat_btn">
            <span
              class="filter"
              @click="handleNewChat"
              @mouseenter="(e) => handleTooltipMouseEnter(e, 'New Chat')"
              @mouseleave="handleTooltipMouseLeave"
            >
              <icon-new-chat width="24" height="24" />
            </span>
          </div>
          <div class="group__list-search-container">
            <user-settings
              :user="currentUser"
              :newUser="newUser"
              :tooltip="tooltipStatus"
              :in-session="inSession"
              @change="handleToggleHiddenChat"
              @hiddenTooltip="handleHiddenTooltip"
              @showTooltip="handleShowTooltip"
            />
            <div class="search-container">
              <div class="search__actions">
                <button
                  class="filter"
                  :class="isGroupSearchVisible ? 'btn-active' : ''"
                  :disabled="groups.length === 0"
                  aria-label="Search"
                  :aria-pressed="isGroupSearchVisible"
                  @click="toggleGroupSearchVisible"
                  @mouseenter="(e) => !isGroupSearchVisible ? handleTooltipMouseEnter(e, 'Search') : undefined"
                  @mouseleave="!isGroupSearchVisible ? handleTooltipMouseLeave() : undefined"
                >
                  <icon-search />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="search-panel" v-if="isGroupSearchVisible">
          <search-input
            placeholder="Search chats"
            small
            @input="handleGroupSearch"
            @transitionend.stop
          />
        </div>

        <tabs
          v-if="inSession"
          :tabs="groupsTabs"
          :active-tab="defaultGroupsTab"
          v-slot="{ active }"
          content-class="users__list users__list--active"
        >
          <template v-if="active === 1 && showInSessionGroupsTab">
            <div class="group__list-content no-scrollbar">
              <ul v-if="sessionGroups.length" class="groups-list">
                <li
                  v-for="group in sessionGroups"
                  :key="group.id"
                  @click="selectGroup(group)"
                  :class="{
                    active:
                      selectedGroup && group.groupId === selectedGroup.groupId,
                  }"
                  @transitionend.stop
                >
                  <div class="users__wrapper users__wrapper--flex">
                    <div class="group__avatars">
                      <div
                        class="group__avatars__images"
                        :class="{
                          'group__avatars__images--more': group.users.length > 2,
                        }"
                      >
                        <div
                          v-for="user in [...group.users].splice(0, 2)"
                          :key="user.user_id"
                          class="group__avatars__images__avatar-container"
                          :class="{
                            'group__avatars__images__avatar-container--more':
                              group.users.length > 2,
                          }"
                        >
                          <img
                            v-if="user.avatar"
                            :src="user.avatar"
                            :alt="user.fullName"
                            :title="user.fullName"
                          />

                          <span
                            v-else
                            class="group__avatars__images__avatar-container--no-avatar"
                          >
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M21.6494 19.8756C20.2216 17.4072 18.0212 15.6372 15.4534 14.7981C16.7236 14.042 17.7104 12.8898 18.2624 11.5185C18.8143 10.1473 18.9009 8.63272 18.5088 7.20749C18.1167 5.78226 17.2676 4.52515 16.0918 3.62921C14.9161 2.73327 13.4788 2.24805 12.0006 2.24805C10.5224 2.24805 9.08512 2.73327 7.90939 3.62921C6.73367 4.52515 5.88455 5.78226 5.49244 7.20749C5.10033 8.63272 5.1869 10.1473 5.73886 11.5185C6.29082 12.8898 7.27765 14.042 8.54781 14.7981C5.98 15.6362 3.77968 17.4062 2.35187 19.8756C2.29951 19.961 2.26478 20.056 2.24973 20.155C2.23468 20.254 2.23961 20.355 2.26424 20.4521C2.28886 20.5492 2.33268 20.6404 2.39311 20.7202C2.45354 20.8001 2.52936 20.867 2.61608 20.9171C2.70281 20.9672 2.79869 20.9995 2.89806 21.0119C2.99744 21.0243 3.09831 21.0167 3.1947 20.9896C3.2911 20.9624 3.38108 20.9162 3.45933 20.8537C3.53758 20.7912 3.60251 20.7136 3.65031 20.6256C5.41656 17.5731 8.53843 15.7506 12.0006 15.7506C15.4628 15.7506 18.5847 17.5731 20.3509 20.6256C20.3987 20.7136 20.4637 20.7912 20.5419 20.8537C20.6202 20.9162 20.7101 20.9624 20.8065 20.9896C20.9029 21.0167 21.0038 21.0243 21.1032 21.0119C21.2026 20.9995 21.2984 20.9672 21.3852 20.9171C21.4719 20.867 21.5477 20.8001 21.6081 20.7202C21.6686 20.6404 21.7124 20.5492 21.737 20.4521C21.7616 20.355 21.7666 20.254 21.7515 20.155C21.7365 20.056 21.7017 19.961 21.6494 19.8756ZM6.75062 9.0006C6.75062 7.96225 7.05853 6.94721 7.6354 6.08385C8.21228 5.2205 9.03222 4.54759 9.99153 4.15023C10.9508 3.75287 12.0064 3.6489 13.0248 3.85147C14.0432 4.05405 14.9787 4.55406 15.7129 5.28829C16.4472 6.02251 16.9472 6.95797 17.1497 7.97637C17.3523 8.99477 17.2483 10.0504 16.851 11.0097C16.4536 11.969 15.7807 12.7889 14.9174 13.3658C14.054 13.9427 13.039 14.2506 12.0006 14.2506C10.6087 14.2491 9.2742 13.6955 8.28995 12.7113C7.30571 11.727 6.75211 10.3925 6.75062 9.0006Z"
                                fill="#4B444D"
                              />
                            </svg>
                          </span>
                        </div>

                        <span>
                          <span
                            v-if="group.users.length > 2"
                            class="group__avatars__images--more-info"
                          >
                            {{
                              group.users.length - 2 > 99
                                ? "99+"
                                : `+${group.users.length - 2}`
                            }}
                          </span>
                        </span>
                      </div>
                    </div>
                    <div class="group__info">
                      <span class="group__name">{{ getUserFullName(group) }}</span>
                      <span
                        v-if="
                          group.lastMessage && Object.keys(group.lastMessage).length
                        "
                        class="group__last__message"
                      >
                        {{
                          `${group.lastMessage.full_name}: ${group.lastMessage.message}`
                        }}
                      </span>
                    </div>
                    <div class="group__time__notifs">
                      <span
                        v-if="
                          group.lastMessage && Object.keys(group.lastMessage).length
                        "
                        class="group__time__notifs__date"
                      >
                        {{ formatGroupMessageTime(group.lastMessage.created_at) }}
                      </span>
                      <notifications-count :count="group.notifications" />
                    </div>
                  </div>
                </li>
              </ul>

              <div v-else class="group__list-content--no-data">
                <span v-if="!groupSearch">No chats yet</span>
                <span v-else>No matching chats found</span>
              </div>
            </div>
          </template>

          <template v-else-if="active === 2">
            <div class="group__list-content no-scrollbar">
              <ul v-if="regularGroups.length" class="groups-list">
                <li
                  v-for="group in regularGroups"
                  :key="group.id"
                  @click="selectGroup(group)"
                  :class="{
                    active:
                      selectedGroup && group.groupId === selectedGroup.groupId,
                  }"
                  @transitionend.stop
                >
                  <div class="users__wrapper users__wrapper--flex">
                    <div class="group__avatars">
                      <div
                        class="group__avatars__images"
                        :class="{
                          'group__avatars__images--more': group.users.length > 2,
                        }"
                      >
                        <div
                          v-for="user in [...group.users].splice(0, 2)"
                          :key="user.user_id"
                          class="group__avatars__images__avatar-container"
                          :class="{
                            'group__avatars__images__avatar-container--more':
                              group.users.length > 2,
                          }"
                        >
                          <img
                            v-if="user.avatar"
                            :src="user.avatar"
                            :alt="user.fullName"
                            :title="user.fullName"
                          />

                          <span
                            v-else
                            class="group__avatars__images__avatar-container--no-avatar"
                          >
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M21.6494 19.8756C20.2216 17.4072 18.0212 15.6372 15.4534 14.7981C16.7236 14.042 17.7104 12.8898 18.2624 11.5185C18.8143 10.1473 18.9009 8.63272 18.5088 7.20749C18.1167 5.78226 17.2676 4.52515 16.0918 3.62921C14.9161 2.73327 13.4788 2.24805 12.0006 2.24805C10.5224 2.24805 9.08512 2.73327 7.90939 3.62921C6.73367 4.52515 5.88455 5.78226 5.49244 7.20749C5.10033 8.63272 5.1869 10.1473 5.73886 11.5185C6.29082 12.8898 7.27765 14.042 8.54781 14.7981C5.98 15.6362 3.77968 17.4062 2.35187 19.8756C2.29951 19.961 2.26478 20.056 2.24973 20.155C2.23468 20.254 2.23961 20.355 2.26424 20.4521C2.28886 20.5492 2.33268 20.6404 2.39311 20.7202C2.45354 20.8001 2.52936 20.867 2.61608 20.9171C2.70281 20.9672 2.79869 20.9995 2.89806 21.0119C2.99744 21.0243 3.09831 21.0167 3.1947 20.9896C3.2911 20.9624 3.38108 20.9162 3.45933 20.8537C3.53758 20.7912 3.60251 20.7136 3.65031 20.6256C5.41656 17.5731 8.53843 15.7506 12.0006 15.7506C15.4628 15.7506 18.5847 17.5731 20.3509 20.6256C20.3987 20.7136 20.4637 20.7912 20.5419 20.8537C20.6202 20.9162 20.7101 20.9624 20.8065 20.9896C20.9029 21.0167 21.0038 21.0243 21.1032 21.0119C21.2026 20.9995 21.2984 20.9672 21.3852 20.9171C21.4719 20.867 21.5477 20.8001 21.6081 20.7202C21.6686 20.6404 21.7124 20.5492 21.737 20.4521C21.7616 20.355 21.7666 20.254 21.7515 20.155C21.7365 20.056 21.7017 19.961 21.6494 19.8756ZM6.75062 9.0006C6.75062 7.96225 7.05853 6.94721 7.6354 6.08385C8.21228 5.2205 9.03222 4.54759 9.99153 4.15023C10.9508 3.75287 12.0064 3.6489 13.0248 3.85147C14.0432 4.05405 14.9787 4.55406 15.7129 5.28829C16.4472 6.02251 16.9472 6.95797 17.1497 7.97637C17.3523 8.99477 17.2483 10.0504 16.851 11.0097C16.4536 11.969 15.7807 12.7889 14.9174 13.3658C14.054 13.9427 13.039 14.2506 12.0006 14.2506C10.6087 14.2491 9.2742 13.6955 8.28995 12.7113C7.30571 11.727 6.75211 10.3925 6.75062 9.0006Z"
                                fill="#4B444D"
                              />
                            </svg>
                          </span>
                        </div>

                        <span>
                          <span
                            v-if="group.users.length > 2"
                            class="group__avatars__images--more-info"
                          >
                            {{
                              group.users.length - 2 > 99
                                ? "99+"
                                : `+${group.users.length - 2}`
                            }}
                          </span>
                        </span>
                      </div>
                    </div>
                    <div class="group__info">
                      <span class="group__name">{{ getUserFullName(group) }}</span>
                      <span
                        v-if="
                          group.lastMessage && Object.keys(group.lastMessage).length
                        "
                        class="group__last__message"
                      >
                        {{
                          `${group.lastMessage.full_name}: ${group.lastMessage.message}`
                        }}
                      </span>
                    </div>
                    <div class="group__time__notifs">
                      <span
                        v-if="
                          group.lastMessage && Object.keys(group.lastMessage).length
                        "
                        class="group__time__notifs__date"
                      >
                        {{ formatGroupMessageTime(group.lastMessage.created_at) }}
                      </span>
                      <notifications-count :count="group.notifications" />
                    </div>
                  </div>
                </li>
              </ul>

              <div v-else class="group__list-content--no-data">
                <span v-if="!groupSearch">No chats yet</span>
                <span v-else>No matching chats found</span>
              </div>
            </div>
          </template>
        </tabs>

        <div v-else class="group__list-content no-scrollbar">
          <ul v-if="regularGroups.length" class="groups-list">
            <li
              v-for="group in regularGroups"
              :key="group.id"
              @click="selectGroup(group)"
              :class="{
                active:
                  selectedGroup && group.groupId === selectedGroup.groupId,
              }"
              @transitionend.stop
            >
              <div class="users__wrapper users__wrapper--flex">
                <div class="group__avatars">
                  <div
                    class="group__avatars__images"
                    :class="{
                      'group__avatars__images--more': group.users.length > 2,
                    }"
                  >
                    <div
                      v-for="user in [...group.users].splice(0, 2)"
                      :key="user.user_id"
                      class="group__avatars__images__avatar-container"
                      :class="{
                        'group__avatars__images__avatar-container--more':
                          group.users.length > 2,
                      }"
                    >
                      <img
                        v-if="user.avatar"
                        :src="user.avatar"
                        :alt="user.fullName"
                        :title="user.fullName"
                      />

                      <span
                        v-else
                        class="group__avatars__images__avatar-container--no-avatar"
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21.6494 19.8756C20.2216 17.4072 18.0212 15.6372 15.4534 14.7981C16.7236 14.042 17.7104 12.8898 18.2624 11.5185C18.8143 10.1473 18.9009 8.63272 18.5088 7.20749C18.1167 5.78226 17.2676 4.52515 16.0918 3.62921C14.9161 2.73327 13.4788 2.24805 12.0006 2.24805C10.5224 2.24805 9.08512 2.73327 7.90939 3.62921C6.73367 4.52515 5.88455 5.78226 5.49244 7.20749C5.10033 8.63272 5.1869 10.1473 5.73886 11.5185C6.29082 12.8898 7.27765 14.042 8.54781 14.7981C5.98 15.6362 3.77968 17.4062 2.35187 19.8756C2.29951 19.961 2.26478 20.056 2.24973 20.155C2.23468 20.254 2.23961 20.355 2.26424 20.4521C2.28886 20.5492 2.33268 20.6404 2.39311 20.7202C2.45354 20.8001 2.52936 20.867 2.61608 20.9171C2.70281 20.9672 2.79869 20.9995 2.89806 21.0119C2.99744 21.0243 3.09831 21.0167 3.1947 20.9896C3.2911 20.9624 3.38108 20.9162 3.45933 20.8537C3.53758 20.7912 3.60251 20.7136 3.65031 20.6256C5.41656 17.5731 8.53843 15.7506 12.0006 15.7506C15.4628 15.7506 18.5847 17.5731 20.3509 20.6256C20.3987 20.7136 20.4637 20.7912 20.5419 20.8537C20.6202 20.9162 20.7101 20.9624 20.8065 20.9896C20.9029 21.0167 21.0038 21.0243 21.1032 21.0119C21.2026 20.9995 21.2984 20.9672 21.3852 20.9171C21.4719 20.867 21.5477 20.8001 21.6081 20.7202C21.6686 20.6404 21.7124 20.5492 21.737 20.4521C21.7616 20.355 21.7666 20.254 21.7515 20.155C21.7365 20.056 21.7017 19.961 21.6494 19.8756ZM6.75062 9.0006C6.75062 7.96225 7.05853 6.94721 7.6354 6.08385C8.21228 5.2205 9.03222 4.54759 9.99153 4.15023C10.9508 3.75287 12.0064 3.6489 13.0248 3.85147C14.0432 4.05405 14.9787 4.55406 15.7129 5.28829C16.4472 6.02251 16.9472 6.95797 17.1497 7.97637C17.3523 8.99477 17.2483 10.0504 16.851 11.0097C16.4536 11.969 15.7807 12.7889 14.9174 13.3658C14.054 13.9427 13.039 14.2506 12.0006 14.2506C10.6087 14.2491 9.2742 13.6955 8.28995 12.7113C7.30571 11.727 6.75211 10.3925 6.75062 9.0006Z"
                            fill="#4B444D"
                          />
                        </svg>
                      </span>
                    </div>

                    <span>
                      <span
                        v-if="group.users.length > 2"
                        class="group__avatars__images--more-info"
                      >
                        {{
                          group.users.length - 2 > 99
                            ? "99+"
                            : `+${group.users.length - 2}`
                        }}
                      </span>
                    </span>
                  </div>
                </div>
                <div class="group__info">
                  <span class="group__name">{{ getUserFullName(group) }}</span>
                  <span
                    v-if="
                      group.lastMessage && Object.keys(group.lastMessage).length
                    "
                    class="group__last__message"
                  >
                    {{
                      `${group.lastMessage.full_name}: ${group.lastMessage.message}`
                    }}
                  </span>
                </div>
                <div class="group__time__notifs">
                  <span
                    v-if="
                      group.lastMessage && Object.keys(group.lastMessage).length
                    "
                    class="group__time__notifs__date"
                  >
                    {{ formatGroupMessageTime(group.lastMessage.created_at) }}
                  </span>
                  <notifications-count :count="group.notifications" />
                </div>
              </div>
            </li>
          </ul>

          <div v-else class="group__list-content--no-data">
            <span v-if="!groupSearch">No chats yet</span>
            <span v-else>No matching chats found</span>
          </div>
        </div>
      </div>
      <modal
        :header="true"
        @close="() => (groupData.visible = false)"
        on-close-text="Cancel"
        on-success-text="Join"
        @success="
          () => {
            groupData.visible = false;
            $emit('enterGroup', groupData.group);
          }
        "
        v-if="groupData.visible"
      >
        <template #header>{{ groupData.title }}</template>
        <div style="padding: 24px 24px 12px">{{ groupData.message }}</div>
      </modal>

      <GroupNameModal
        v-if="groupCreateModal.visible"
        :current-group-name="groupCreateModal.name"
        @confirm="(name) => createGroup(name)"
        @cancel="() => (groupCreateModal.visible = false)"
      />

      <BreakoutRoomModal
        v-if="breakoutModalData"
        :participant-identity="breakoutModalData.participantIdentity"
        :participant-name="breakoutModalData.participantName"
        :rooms="sessionStore.breakoutRooms"
        @move="handleBreakoutMove"
        @close="breakoutModalData = null"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.e-box-container--full .users-search-input-action {
  display: none;
}

/* Peer-to-peer messaging restriction: regular members stay visible but read-only.
   pge-button's container is height:100% and its content stretches — pin both so it
   renders as a compact icon inside the flex row (pge-component gotcha). */
.users__restriction-icon {
  height: auto;
}

.users__restriction-icon :deep(.pge-button) {
  background: transparent;
}

.users__restriction-icon :deep(.pge-button-content) {
  padding: 4px !important;
  flex: none;
  align-self: center;
}

.users__restriction-icon :deep(.pge-icon svg) {
  width: 18px;
  height: 18px;
}

.users-bulk-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-right: 6px;
}

/* Breakout status inside the People list (in-session only), in the room's
   breakout language (.botop / .bostat). */
.breakout-tenant-controls {
  padding: 12px 14px 10px;
  background: #fff;
  border-bottom: 1px solid var(--xl-color-border-subtle, #F6F2F8);
}

.breakout-stats-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.breakout-stats-left {
  display: flex;
  align-items: center;
  min-width: 0;
}

.breakout-room-name {
  font-weight: 600;
  font-size: 13.5px;
  color: var(--xl-color-on-surface, #1C1720);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 120px;
}

.breakout-stats-right {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  font-weight: 600;
  color: var(--xl-color-outline, #867D8D);
}

.breakout-stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--xl-color-outline, #867D8D);
}

.breakout-stat-divider {
  width: 1px;
  height: 14px;
  background-color: var(--xl-color-border, #EDE7F0);
  margin: 0 2px;
}

.breakout-stat-live {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--xl-color-error, #A32020);
  font-weight: 600;
  font-size: 12px;
}

.breakout-live-dot {
  width: 6px;
  height: 6px;
  background-color: var(--xl-color-error, #A32020);
  border-radius: 50%;
  animation: breakout-pulse-red 2s infinite;
}

@keyframes breakout-pulse-red {
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .breakout-live-dot {
    animation: none;
  }
}

.breakout-stat-time {
  color: var(--xl-color-on-surface-variant, #514A57);
  font-variant-numeric: tabular-nums;
}

.breakout-action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.text-action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 0;
  color: #4B444D;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
}

.text-action-btn:hover:not(:disabled) {
  opacity: 0.7;
}

.text-action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ===================================================================
   Northstar: the in-session panel's chat list and People list
   (prototype §2, §6, §9). Applies only under the panel's
   [data-xl-gen="northstar"] root; the hub-wide chat outside it keeps the
   Generation 1 look from chat.scss. chat.scss nests these selectors
   several classes deep, so the root class is repeated to outrank it
   without !important.
   =================================================================== */
$ns: '[data-xl-gen="northstar"] .xl-rm.xl-rm.xl-rm.xl-rm';

$primary: var(--xl-color-primary, #5E2F78);
$tint-2: var(--xl-color-surface-tint, #8154A6);
$tint: var(--xl-color-primary-container, #F5EFF9);
$tint-soft: var(--xl-color-primary-fixed-dim, #EADFF3);
$tint-3: #D9C6E8;
$line: var(--xl-color-border, #EDE7F0);
$line-2: var(--xl-color-border-subtle, #F6F2F8);
$ink: var(--xl-color-on-surface, #1C1720);
$ink-2: var(--xl-color-on-surface-variant, #514A57);
$mut: var(--xl-color-outline, #867D8D);
$faint: var(--xl-color-outline-variant, #B6AEBC);
$cr: var(--xl-color-error, #A32020);
$ok: var(--xl-color-success, #166B48);
$paper: var(--xl-color-surface, #FCFAFD);
$font: var(--xl-font-family, 'General Sans', Inter, -apple-system, 'Segoe UI', sans-serif);
$grad: linear-gradient(155deg, $tint-2, $primary);
$face: linear-gradient(150deg, $tint-2, $primary);
$sh: 0 1px 2px rgba(28, 23, 32, 0.04);

#{$ns} {
  font-family: $font;
  letter-spacing: -0.006em;
  color: $ink;
  background: #fff;

  button,
  input {
    font-family: $font;
  }

  /* ---- Sub-tabs: "In Session / Hub-wide" on both lists. The room's small
     grey-track segmented control (.rmsub .seg). */
  :deep(.tabs__items) {
    gap: 2px;
    margin: 10px 12px 2px;
    padding: 3px;
    background: $line-2;
    border-radius: 10px;

    .tab {
      flex: 1;
      width: auto;
      min-width: 0;
      height: 26px;
      padding: 0 6px;
      border: 0;
      border-radius: 7px;
      font-size: 12px;
      font-weight: 600;
      line-height: 1;
      color: $mut;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: background-color 0.15s, color 0.15s;

      &:hover {
        color: $ink-2;
      }

      &.active {
        border: 0;
        background: #fff;
        color: $primary;
        font-weight: 600;
        box-shadow: $sh;
      }
    }
  }

  /* ---- Header rows: select-all, Hide me in chat, the icon buttons */
  .users__list-header,
  .group__list-header {
    min-height: 0;
    height: auto;
    gap: 4px;
    padding: 6px 10px 6px 4px;
    background: #fff;
    border-bottom: 1px solid $line;
  }

  .group__list-header {
    padding-left: 10px;
  }

  .users__list-header .custom-check {
    padding: 0;
    border-right: 0;
  }

  .group__list-header .new_chat_btn {
    height: auto;
    padding: 0;
    border-right: 0;
  }

  .search__actions {
    min-height: 0;
    gap: 4px;

    .filter {
      display: grid;
      place-items: center;
      width: 32px;
      height: 32px;
      padding: 0;
      border-radius: 9px;
      color: $mut;
      transition: background-color 0.15s, color 0.15s;

      svg {
        width: 20px;
        height: 20px;
      }

      svg path {
        fill: currentColor;
      }

      &:hover:not(:disabled) {
        background-color: $line-2;
        color: $ink;
      }

      &.btn-active {
        background-color: $tint;
        color: $primary;
      }

      &:disabled {
        opacity: 0.4;
        cursor: default;
      }

      &:focus-visible {
        outline: 2px solid $tint-2;
        outline-offset: 1px;
      }
    }
  }

  /* New chat is the list's one action: a soft tinted square. */
  .new_chat_btn .filter {
    background-color: $tint;
    color: $primary;

    &:hover:not(:disabled) {
      background-color: $tint-soft;
      color: $primary;
    }
  }

  .search-panel {
    padding: 8px 12px 4px;
  }

  .search-panel :deep(.input__container) {
    height: 36px;
    padding: 0 12px;
    border: 1px solid $line;
    border-radius: 99px;
    background: #fff;
    font-size: 13px;

    span svg path {
      fill: $mut;
    }

    input {
      font-family: $font;
      font-size: 13px;
      line-height: 20px;
      color: $ink;
      caret-color: $primary;
      text-indent: 2px;

      &::placeholder {
        color: $faint;
      }
    }

    &:focus-within {
      padding: 0 12px;
      border: 1px solid $primary;
      box-shadow: 0 0 0 3px rgba(94, 47, 120, 0.1);
    }
  }

  /* ---- Checkboxes (select all, and each person) */
  :deep(.pge-checkbox .state-layer) {
    padding: 8px;
  }

  :deep(.pge-checkbox .state-layer .pge-container) {
    width: 16px;
    height: 16px;
    border-radius: 5px;
  }

  :deep(input[type='checkbox']:not(:checked) + .pge-checkbox .state-layer .pge-container) {
    border: 1.5px solid $faint;
    background: #fff;
  }

  :deep(input[type='checkbox']:checked + .pge-checkbox .pge-container) {
    background: $grad;
  }

  :deep(input[type='checkbox']:focus-visible + .pge-checkbox .state-layer) {
    box-shadow: 0 0 0 2px $tint-2;
  }

  /* ---- Chat list rows (.rmrow) */
  .users__list .groups-list {
    padding: 4px 0 8px;

    li {
      height: auto;
      padding: 10px 14px;
      border-bottom: 1px solid $line-2;
      transition: background-color 0.15s;

      &:last-child {
        border-bottom: 0;
      }

      &:hover {
        background-color: $paper;
      }

      &.active {
        background-color: $tint;
      }
    }
  }

  .users__wrapper--flex {
    height: auto;
    column-gap: 10px;
  }

  .group__avatars {
    flex: 0 0 40px;
  }

  .group__avatars__images__avatar-container {
    img {
      box-shadow: 0 0 0 1.5px #fff;
    }
  }

  .group__avatars__images__avatar-container--no-avatar {
    background: $face;
    box-shadow: 0 0 0 1.5px #fff;

    svg {
      margin-top: 3px;
    }

    svg path {
      fill: #fff;
    }
  }

  .group__avatars__images--more-info {
    width: 18px;
    height: 18px;
    border: 1px solid $line;
    background-color: #fff;
    font-size: 9.5px;
    font-weight: 700;
    line-height: 1;
    color: $ink-2;
  }

  .group__info {
    row-gap: 1px;

    .group__name {
      font-size: 13.5px;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: -0.01em;
      color: $ink;
    }

    .group__last__message {
      font-size: 11.5px;
      line-height: 16px;
      letter-spacing: 0;
      color: $mut;
    }
  }

  .users__wrapper .group__time__notifs {
    align-items: flex-end;
    row-gap: 4px;
  }

  .users__wrapper .group__time__notifs__date {
    font-size: 11px;
    font-weight: 500;
    line-height: 16px;
    letter-spacing: 0;
    color: $faint;
  }

  :deep(.notifications__count) {
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    background-color: $cr;

    span {
      font-size: 10px;
      font-weight: 700;
      line-height: 1;
    }
  }

  .users__list .group__list-content--no-data,
  .users__list--active .no--users {
    height: auto;
    padding: 26px 20px;
    border-radius: 0;

    span {
      font-size: 13px;
      line-height: 1.5;
      color: $mut;
      text-align: center;
    }
  }

  /* ---- Hub-wide people rows */
  .users__list ul.users__list-body {
    padding: 4px 0 76px;

    li {
      height: auto;
      min-height: 52px;
      padding: 4px 12px 4px 4px;
      column-gap: 4px;
      border-bottom: 1px solid $line-2;
      transition: background-color 0.15s;

      &:last-child {
        border-bottom: 0;
      }

      &:hover {
        background-color: $paper;
      }
    }
  }

  .users__picture {
    margin-right: 10px;

    .avatar {
      background: $face;

      svg rect {
        stroke: transparent;
      }

      svg path {
        fill: #fff;
      }
    }
  }

  .users__status {
    width: 10px;
    height: 10px;
    border: 2px solid #fff;

    &--online {
      background-color: $ok;
    }

    &--offline {
      background-color: $faint;
    }
  }

  .users__wrapper strong {
    font-size: 13.5px;
    font-weight: 600;
    line-height: 18px;
    letter-spacing: -0.01em;
    color: $ink;
  }

  .users__info_button {
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    padding: 0;
    border-radius: 9px;
    color: $mut;

    svg path {
      fill: currentColor;
    }

    &:hover {
      background-color: $line-2;
      color: $ink;
    }
  }

  /* ---- In-session people list (.rmppl) */
  .people-list {
    padding: 4px 14px 76px;
  }

  /* Mute All / Stop Video / Leave Breakout / Switch Rooms: the quiet
     in-row button (.btn.g.sm at the panel's 28px row size). */
  :deep(.users-quiet-btn.pge-button-container) {
    height: auto;

    .pge-button {
      height: 28px;
      border: 1px solid $line;
      border-radius: 99px;
      background-color: #fff !important;
      box-shadow: $sh;
      transition: background-color 0.15s, border-color 0.15s;

      &:hover {
        background-color: $tint !important;
        border-color: $tint-3;
      }

      &:focus-visible {
        outline: 2px solid $tint-2;
        outline-offset: 1px;
      }
    }

    .pge-button-content {
      gap: 5px;
      padding: 0 11px !important;
      font-family: $font;
      font-size: 12px;
      font-weight: 600;
      line-height: 1;
      letter-spacing: 0;
      color: $ink-2 !important;
      white-space: nowrap;
    }

    .pge-icon {
      color: $ink-2 !important;
    }

    .pge-icon svg {
      width: 15px;
      height: 15px;
    }
  }

  .breakout-action-buttons :deep(.users-quiet-btn .pge-button) {
    height: 30px;
  }

  /* ---- New Chat: the soft pill in the corner (.pnew); quiet until somebody
     is selected. */
  .start-group {
    width: auto;
    height: auto;
    right: 14px;
    bottom: 14px;

    button {
      height: 40px;
      gap: 8px;
      padding: 0 18px;
      border: 1px solid $line;
      border-radius: 99px;
      background: #fff;
      color: $mut;
      box-shadow: none;
      transition: background-color 0.14s, box-shadow 0.14s, color 0.14s;

      .state-layer {
        display: none;
      }

      span {
        font-size: 13.5px;
        font-weight: 600;
        line-height: 1;
        letter-spacing: 0;
      }

      svg {
        width: 18px;
        height: 18px;
      }

      svg path {
        fill: currentColor;
      }

      &:focus-visible {
        outline: 2px solid $tint-2;
        outline-offset: 2px;
      }
    }

    &.active button {
      border-color: transparent;
      background: $tint;
      color: $primary;
      box-shadow: 0 2px 10px rgba(96, 46, 138, 0.12);

      &:hover,
      &:focus {
        background: $tint-soft;
        box-shadow: 0 4px 14px rgba(96, 46, 138, 0.18);
      }
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  #{$ns} {
    .search__actions .filter,
    .groups-list li,
    ul.users__list-body li,
    .start-group button,
    :deep(.tabs__items .tab),
    :deep(.pge-button) {
      transition: none;
    }
  }
}


/* People in a session is one list: its tab strip is not drawn. */
.people-tabs--single :deep(.tabs__items) { display: none; }
</style>
